import { createContext, useContext, useState, useEffect } from "react";

const InquiryContext = createContext();

// Helper to extract numeric price from strings like "149 kr.", "Fra 49 kr.", "49 kr. pr. stk"
export const parsePriceNumber = (priceStr) => {
  if (!priceStr) return 0;
  if (typeof priceStr === "number") return priceStr;
  const match = priceStr.replace(/\./g, "").match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

export const formatPrice = (num) => {
  if (!num || isNaN(num)) return "0 kr.";
  return `${num.toLocaleString("da-DK")} kr.`;
};

const STORAGE_KEY = "hj3d_inquiry_items";

export const InquiryProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sync with localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error("Failed to save inquiry items to localStorage", e);
    }
  }, [items]);

  const addItem = (product, quantity = 1) => {
    if (!product) return;
    const priceNum = parsePriceNumber(product.price);

    setItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === product.id || item.name === product.name
      );

      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      } else {
        return [
          ...prev,
          {
            id: product.id || `prod-${Date.now()}-${Math.random()}`,
            name: product.name,
            price: product.price,
            priceNum: priceNum,
            image: product.image,
            quantity: quantity,
          },
        ];
      }
    });
  };

  const removeItem = (idOrName) => {
    setItems((prev) =>
      prev.filter((item) => item.id !== idOrName && item.name !== idOrName)
    );
  };

  const updateQuantity = (idOrName, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(idOrName);
      return;
    }
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === idOrName || item.name === idOrName) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const clearItems = () => {
    setItems([]);
  };

  const openInquiry = (product = null) => {
    if (product) {
      addItem(product, 1);
    }
    setIsModalOpen(true);
  };

  const closeInquiry = () => {
    setIsModalOpen(false);
  };

  const totalCount = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const totalNumericPrice = items.reduce(
    (sum, item) => sum + (item.priceNum || 0) * (item.quantity || 1),
    0
  );
  const totalPrice = formatPrice(totalNumericPrice);

  const getItemQuantity = (idOrName) => {
    const found = items.find(
      (item) => item.id === idOrName || item.name === idOrName
    );
    return found ? found.quantity : 0;
  };

  return (
    <InquiryContext.Provider
      value={{
        items,
        totalCount,
        totalNumericPrice,
        totalPrice,
        isModalOpen,
        addItem,
        removeItem,
        updateQuantity,
        clearItems,
        openInquiry,
        closeInquiry,
        getItemQuantity,
      }}
    >
      {children}
    </InquiryContext.Provider>
  );
};

export const useInquiry = () => {
  const context = useContext(InquiryContext);
  if (!context) {
    throw new Error("useInquiry must be used within an InquiryProvider");
  }
  return context;
};
